import type { LinkEvent } from '@/core/features/router/router-link';
import type { IMapAccommodationTile } from '@/features/map/map-data-v5/map-types';
import type { TopLivePrice } from '@/features/top-live-price/top-live-price-type';

import React from 'react';

import { Divider, HtmlTextWrapper, IconImage, Link } from '@/core/features';
import useLinkoutTracking from '@/features/linkout-tracking/use-linkout-tracking';
import { MapAccommodationTileContent } from '@/features/map/map-components/map-tiles/accommodation-tiles/accommodation-desktop/map-accommodation-tile-content';
import MapTileWrapper from '@/features/map/map-components/map-tiles/map-tile-wrapper';

type CtaListOnClickInterface = (event: LinkEvent, cta: TopLivePrice) => boolean | void;

export type MapAccommodationTileDesktopProps = IMapAccommodationTile & {
    hasSelectedVertical: boolean;
};

export default function MapAccommodationTileDesktop({
    accommodationTypeBadge,
    activity,
    badges,
    hasSelectedVertical,
    highlightedBadges,
    image,
    keyfacts,
    name,
    rating,
    spotName,
    stars,
    topLivePrices,
}: MapAccommodationTileDesktopProps) {
    const { trackLinkout } = useLinkoutTracking();

    const [isTileOpen, setTileOpen] = React.useState(false);
    const isHighlighted = highlightedBadges?.length > 0;

    const handleCtaLinkoutClick = React.useCallback<CtaListOnClickInterface>(
        (event, cta) => {
            event.stopPropagation();
            trackLinkout({
                activity,
                elementType: 'accommodation_tile_map',
                name,
                spot: spotName,
                verticalType: cta.type,
            });
        },
        [trackLinkout, activity, name, spotName],
    );

    React.useEffect(() => {
        if (hasSelectedVertical) {
            setTileOpen(true);
        }
    }, [hasSelectedVertical]);

    return (
        <>
            <MapTileWrapper
                hasTopLivePrices={!!topLivePrices?.ctaList?.length && topLivePrices.ctaList.length > 0}
                height={'217px'}
                highlightedBadges={highlightedBadges}
                image={image}
                imageWidth={120}
                isDesktopView={true}
                linkType={'vertical'}
                linkUrl={hasSelectedVertical ? topLivePrices.ctaList[0]?.linkUrl : undefined}
                qaId={'qa-map-accommodation-tile-desktop'}
                target={'_blank'}
                width={'369px'}
            >
                <MapAccommodationTileContent
                    accommodationTypeBadge={accommodationTypeBadge}
                    badges={badges}
                    hasSelectedVertical={hasSelectedVertical}
                    highlightedBadges={highlightedBadges}
                    keyfacts={keyfacts}
                    name={name}
                    rating={rating}
                    stars={stars}
                    toggleTileOpen={() => {
                        if (!hasSelectedVertical) {
                            setTileOpen(!isTileOpen);
                        }
                    }}
                />
            </MapTileWrapper>
            <Divider color={'alto'} />
            {!hasSelectedVertical && (
                <div
                    className={`${!isTileOpen ? 'border-radius-bottom' : ''} ${isHighlighted ? 'bg-color-foam' : ''} flex-end width-100 padding-10 font-size-14 gap-3 align-center bg-white`}
                    data-qa-id={'qa-top-live-prices-cta'}
                    onClick={() => setTileOpen(!isTileOpen)}
                >
                    <HtmlTextWrapper htmlText={isTileOpen ? 'verbergen' : topLivePrices.htmlText} />
                    <div
                        className={'margin-left-5 flex justify-center'}
                        style={{
                            transform: isTileOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            transition: 'transform 0.3s',
                        }}
                    >
                        {topLivePrices && (
                            <IconImage
                                height={14}
                                url={topLivePrices.iconUrl}
                                width={14}
                            />
                        )}
                    </div>
                </div>
            )}
            <div
                className={`bg-white border-radius-bottom overflow-hidden ${isHighlighted ? 'bg-color-foam' : ''} pointer`}
                style={{
                    padding: hasSelectedVertical ? '10px' : isTileOpen ? '0 10px 10px 10px' : '0 10px',
                }}
            >
                <div
                    className={`${!hasSelectedVertical ? 'livePriceGrid' : ''} ${isTileOpen ? 'livePriceGridOpen' : ''}`}
                    data-qa-id={'qa-top-live-prices-cta'}
                >
                    <div className={'flex-column gap-10 shrink-0 overflow-hidden'}>
                        {topLivePrices.ctaList.map((topLivePrice, index) => (
                            <Link
                                fullWidth={true}
                                href={topLivePrice.linkUrl}
                                isFlex={true}
                                key={`${topLivePrice.htmlTitle}-${index}`}
                                linkType={'vertical'}
                                onClick={(event) => handleCtaLinkoutClick(event, topLivePrice)}
                                target={'_blank'}
                            >
                                <div
                                    className={
                                        'flex-space-between-horizontal padding-10 gap-5 border border-alto border-radius-5'
                                    }
                                >
                                    <div className={'flex gap-10 align-center'}>
                                        <IconImage
                                            height={18}
                                            url={topLivePrice.iconUrl}
                                            width={18}
                                        />
                                        <span className={'line-clamp-2 font-size-14'}>
                                            <HtmlTextWrapper htmlText={topLivePrice.htmlTitle} />
                                        </span>
                                    </div>
                                    <div className={'gap-3" flex-end flex-column'}>
                                        <div className={'flex-end gap-3'}>
                                            {topLivePrice.price.prefixText && (
                                                <span className={'font-size-12'}>{topLivePrice.price.prefixText}</span>
                                            )}
                                            <HtmlTextWrapper
                                                className={'font-size-14 bold'}
                                                htmlText={topLivePrice.price.htmlText}
                                            />
                                        </div>
                                        {topLivePrice.price.subtitle && (
                                            <span className={'font-size-12'}>{topLivePrice.price.subtitle}</span>
                                        )}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
            <style jsx>{`
                .livePriceGrid {
                    display: grid;
                    grid-template-rows: 0fr;
                    transition:
                        grid-template-rows 0.3s ease-out,
                        all 0.3s ease-out;
                    opacity: 0;
                }
                .livePriceGridOpen {
                    grid-template-rows: 1fr;
                    opacity: 1;
                }
            `}</style>
        </>
    );
}
